import React from 'react'
import { Lock, Zap, Check } from 'lucide-react'

// The Studio launch promo, as /api/user/plan reports it: until the end of `until`, anyone
// signed in with Discord can use Studio. The server decides; this only shows it.
export interface StudioPromo {
  active: boolean
  until: string
  discord: boolean
}

interface PlanUpgradePromptProps {
  promo?: StudioPromo | null
}

// Studio runs inside an iframe on www.legit2clip.com/studio, and on its own at
// studio.legit2clip.com. Pro and sign-in pages live on the main site, and the sign-in page
// cannot be framed, so these links always open in the top window.
const SITE = window.location.hostname.startsWith('studio.') ? 'https://www.legit2clip.com' : ''

function goTop(path: string) {
  const url = SITE + path
  try {
    ;(window.top ?? window).location.href = url
  } catch {
    window.location.href = url
  }
}

export function PlanUpgradePrompt({ promo }: PlanUpgradePromptProps) {
  const promoOpen = !!promo?.active

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-indigo-900 flex items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-600/20 rounded-full mb-6">
            <Lock className="w-10 h-10 text-indigo-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Studio is part of Pro
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            The timeline editor for your own videos: pick highlights, trim them on the
            transcript, and export captioned clips.
          </p>
        </div>

        {/* Launch promo: free with Discord sign-in until the date the server gives */}
        {promoOpen && (
          <div className="mb-10 rounded-xl border-2 border-[#765CF7] bg-[#765CF7]/15 p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">
              Free with Discord sign-in until {promo!.until}
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto mb-6">
              Until the end of {promo!.until}, anyone signed in with Discord can use Studio.
              Jobs you run bill your credits at the normal price, and clips keep the
              watermark unless you have Pro.
            </p>
            <button
              onClick={() => goTop('/auth/sso/login?next=/studio')}
              className="bg-[#765CF7] hover:bg-[#826DFA] text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200"
            >
              Continue with Discord
            </button>
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Free Plan */}
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
            <h3 className="text-lg font-semibold text-gray-300 mb-2">
              Free
            </h3>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white">$0</span>
              <span className="text-gray-300">/month</span>
            </div>
            <ul className="space-y-3">
              {[
                '5 hours of video every month with Discord sign-in',
                'Auto highlights with captions',
                'Small legit2clip watermark',
                promoOpen ? `Studio until ${promo!.until}` : 'No Studio'
              ].map((feature, i) => (
                <li key={i} className="flex items-center text-gray-400">
                  <Check className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pro Plan */}
          <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 backdrop-blur-sm border-2 border-indigo-500/50 rounded-xl p-8 relative overflow-hidden">
            <h3 className="text-lg font-semibold text-white mb-2">
              Pro
            </h3>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white">$19</span>
              <span className="text-gray-300">/month</span>
            </div>

            <ul className="space-y-3">
              {[
                'The Studio editor',
                'No watermark',
                'Our most accurate transcription',
                'Half price on every clip (2¢ a minute)',
                'Cancel any time'
              ].map((feature, i) => (
                <li key={i} className="flex items-center text-white">
                  <Zap className="w-5 h-5 text-indigo-400 mr-3 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => goTop('/pro?from=studio')}
              className="w-full mt-8 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
            >
              <Zap className="w-5 h-5" />
              <span>Go Pro</span>
            </button>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-gray-400 mb-4">
            Already have Pro? It can take a minute to show up here.{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); goTop('/pro') }} className="text-indigo-400 hover:text-indigo-300 underline">Check the Pro page</a>
          </p>
          <p className="text-sm text-gray-500">
            Questions? <a href="#" onClick={(e) => { e.preventDefault(); goTop('/contact') }} className="text-indigo-400 hover:text-indigo-300">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  )
}
